/**
 * Short timezone label for chart headers (e.g. IST, PST) instead of IANA IDs like Asia/Calcutta.
 */

const ZONE_TO_ABBREV = {
  'Asia/Calcutta': 'IST',
  'Asia/Kolkata': 'IST',
}

/**
 * @param {string | undefined} ianaTimeZone
 * @returns {string} abbreviation when known, else Intl short name, else original IANA id
 */
export function chartHeaderTimezoneAbbrev(ianaTimeZone) {
  if (!ianaTimeZone || typeof ianaTimeZone !== 'string') return ''

  if (ZONE_TO_ABBREV[ianaTimeZone]) return ZONE_TO_ABBREV[ianaTimeZone]

  try {
    const parts = new Intl.DateTimeFormat(undefined, {
      timeZone: ianaTimeZone,
      timeZoneName: 'short',
    }).formatToParts(new Date())
    const abbr = parts.find((p) => p.type === 'timeZoneName')?.value?.trim() ?? ''
    if (abbr) return abbr
  } catch {
    /* invalid zone */
  }

  return ianaTimeZone
}

/**
 * Full header fragment, e.g. "Times shown in IST"
 * @param {string | undefined} ianaTimeZone
 * @returns {string}
 */
export function chartHeaderTimesShownLine(ianaTimeZone) {
  const abbr = chartHeaderTimezoneAbbrev(ianaTimeZone)
  return abbr ? `Times shown in ${abbr}` : ''
}

/**
 * Resolve IANA timezone for chart heatmaps from property settings.
 * @param {string | null | undefined} propertyTimezone
 * @returns {string}
 */
export function resolvePropertyChartTimezone(propertyTimezone) {
  const candidates = [propertyTimezone, 'UTC'].filter(Boolean)
  for (const tz of candidates) {
    const value = String(tz).trim()
    if (!value) continue
    try {
      Intl.DateTimeFormat(undefined, { timeZone: value })
      return value
    } catch {
      /* not a valid IANA zone */
    }
  }
  return 'UTC'
}
